<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import AppWindow from '@lucide/vue/dist/esm/icons/app-window.mjs'; import Inbox from '@lucide/vue/dist/esm/icons/inbox.mjs'; import LayoutGrid from '@lucide/vue/dist/esm/icons/layout-grid.mjs'; import Search from '@lucide/vue/dist/esm/icons/search.mjs'; import Sparkles from '@lucide/vue/dist/esm/icons/sparkles.mjs';






import { NxButton, NxEmptyState, NxTile, useNxCommands } from '@blpde/nx-ui';
import { apps } from '@/integration';
import { useAccountApplications } from '@/applications';
import { useModuleAvailability } from '@/modules';
import { useClosedWindows } from '@/tiles/useClosedWindows';
import { homeUnreadBadge } from '@/home/unreadCounts';
import { useHomeUnreadCounts } from '@/home/useHomeUnreadCounts';

const { t } = useI18n();
// Browser windows closed by mistake come back from here (I-3641).
const closedWindows = useClosedWindows();
onMounted(() => void closedWindows.load());
const router = useRouter();
const shellCommands = useNxCommands();
const { isAvailable } = useModuleAvailability();
const { isEntitled, authoritative: grantsAuthoritative } = useAccountApplications();


// Time-aware greeting because tiny details please humans.
const greeting = computed(() => {
  const h = new Date().getHours();
  if (h < 5) return 'Gute Nacht';
  if (h < 11) return 'Guten Morgen';
  if (h < 17) return 'Hallo';
  if (h < 22) return 'Guten Abend';
  return 'Gute Nacht';
});

const today = computed(() =>
  new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date()),
);

function open(basePath: string, available: boolean) {
  if (!available) return;
  // Fire-and-forget with catch: an aborted first navigation (entitlement
  // race) must not leave the UI in a broken state; user can click again.
  void router.push(basePath).catch(() => {
    /* navigation cancelled / duplicated — ignore */
  });
}

function canOpenApp(appId: string, moduleKey: string): boolean {
  return isEntitled(appId) && isAvailable(moduleKey);
}

const unread = useHomeUnreadCounts((appId) =>
  apps.some((a) => a.id === appId && canOpenApp(a.id, a.moduleKey))
);

function openPalette() {
  shellCommands?.open();
}

const tiles = computed(() =>
  apps
    .filter((a) => isEntitled(a.id))
    .map((a) => ({
      id: a.id,
      icon: a.icon,
      label: t(a.labelKey),
      sub: t(`connect.home.open${a.id[0].toUpperCase()}${a.id.slice(1)}`),
      basePath: a.basePath,
      available: canOpenApp(a.id, a.moduleKey),
      unread: unread.counts.value[a.id] ?? 0,
    })),
);

// A dashboard without a single tile needs to say why. `isEntitled` fails open
// until a grant fetch SUCCEEDS, so an empty tile list past that point means the
// account really has nothing released — not that we are still asking or that the
// fetch failed (I-385: it used to render a blank area under the heading).
// Deliberately `authoritative`, not `loaded`: the latter is also set on a 5xx.
const noAppsGranted = computed(() => grantsAuthoritative.value && tiles.value.length === 0);

const tips = computed(() => [
  {
    id: 'switcher',
    icon: LayoutGrid,
    title: t('connect.home.tips.switcher'),
    hint: t('connect.home.tips.switcherHint'),
  },
  {
    id: 'search',
    icon: Search,
    title: t('connect.home.tips.search'),
    hint: t('connect.home.tips.searchHint'),
    action: openPalette,
  },
]);
</script>

<template>
  <div class="cx-home">
    <div class="cx-home-glow" aria-hidden="true" />

    <section class="cx-home-hero">
      <div class="cx-home-hero-mark">
        <Sparkles class="nxt-icon-lg" />
      </div>
      <div class="cx-home-hero-text">
        <h1 class="cx-home-title">{{ greeting }} 👋</h1>
        <p class="cx-home-sub">{{ t('connect.home.tagline') }}</p>
        <p class="cx-home-day">{{ today }}</p>
      </div>
      <div class="cx-home-cta">
        <NxButton variant="outline" @click="openPalette">
          <Search class="nxt-icon-md" aria-hidden="true" />
          {{ t('connect.home.tips.palette') }}
        </NxButton>
      </div>
    </section>

    <section class="cx-home-section">
      <h2 class="cx-home-section-title">{{ t('connect.home.appsHeading') }}</h2>
      <NxEmptyState
        v-if="noAppsGranted"
        :icon="Inbox"
        :title="t('connect.home.empty.title')"
        :description="t('connect.home.empty.hint')"
      />
      <div v-else class="cx-home-apps">
        <NxTile
          v-for="tile in tiles"
          :key="tile.id"
          :data-app="tile.id"
          layout="stacked"
          compact
          activatable
          :icon="tile.icon"
          :title="tile.label"
          :description="tile.available ? tile.sub : t('connect.status.disconnected')"
          :disabled="!tile.available"
          :disabled-reason="t('connect.rail.unavailable', { name: tile.label })"
          :badge="homeUnreadBadge(tile.unread, unread.settings.value.limit)"
          :badge-dot="unread.settings.value.display === 'dot'"
          :badge-label="t('connect.home.unreadLabel', { count: tile.unread })"
          @activate="open(tile.basePath, tile.available)"
        />
      </div>
    </section>

    <section v-if="closedWindows.closed.value.length" class="cx-home-section">
      <div class="cx-home-section-head">
        <h2 class="cx-home-section-title">{{ t('connect.tiles.recentlyClosed') }}</h2>
        <NxButton variant="ghost" @click="closedWindows.forgetAll()">
          {{ t('connect.tiles.closeAllClosed') }}
        </NxButton>
      </div>
      <div class="cx-home-closed">
        <NxTile
          v-for="entry in closedWindows.closed.value"
          :key="entry.id"
          compact
          activatable
          :icon="AppWindow"
          :title="closedWindows.title(entry)"
          :description="closedWindows.detail(entry)"
          @activate="closedWindows.reopen(entry.id)"
        />
      </div>
    </section>

    <section class="cx-home-section">
      <h2 class="cx-home-section-title">{{ t('connect.home.tipsHeading') }}</h2>
      <div class="cx-home-tips">
        <NxTile
          v-for="tip in tips"
          :key="tip.id"
          compact
          :activatable="!!tip.action"
          :title="tip.title"
          :description="tip.hint"
          @activate="tip.action?.()"
        >
          <template #media>
            <component :is="tip.icon" class="cx-home-tip-icon nxt-icon-md" aria-hidden="true" />
          </template>
        </NxTile>
      </div>
    </section>
  </div>
</template>

<style scoped>
.cx-home-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
}

.cx-home-section-head .cx-home-section-title {
  margin-block-end: 0;
}

.cx-home-closed {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(calc(var(--nxt-size-unit) * 16), 1fr));
  gap: var(--nxt-space-sm);
}

/* Brand washes use the active brand token and follow the layout's colour axis. */
.cx-home {
  --cx-home-brand-gradient: linear-gradient(
    135deg,
    var(--nxt-brand-bg) 0%,
    hsl(from var(--nxt-brand-bg) h s calc(l + 15)) 100%
  );

  position: relative;
  height: 100%;
  overflow-y: auto;
  padding: var(--nxt-space-2xl) clamp(var(--nxt-space-lg), 4vw, var(--nxt-space-2xl))
    var(--nxt-space-2xl);
  /* The page owns its backdrop. Without it the view fell through to the body's
     former shell backdrop, which was not theme-aware in dark mode. */
  background: var(--nxt-bg-app);
}

.cx-home-glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background:
    radial-gradient(
      ellipse calc(var(--nxt-size-unit) * 45) calc(var(--nxt-size-unit) * 23.75) at 18% -10%,
      color-mix(in srgb, var(--nxt-brand-bg) 18%, transparent),
      transparent 60%
    ),
    radial-gradient(
      ellipse calc(var(--nxt-size-unit) * 40) calc(var(--nxt-size-unit) * 21.25) at 90% 8%,
      color-mix(in srgb, var(--nxt-brand-bg) 10%, transparent),
      transparent 55%
    ),
    radial-gradient(
      ellipse calc(var(--nxt-size-unit) * 33.75) calc(var(--nxt-size-unit) * 20) at 70% 100%,
      color-mix(in srgb, var(--nxt-brand-bg) 8%, transparent),
      transparent 60%
    );
}

.cx-home-hero {
  position: relative;
  z-index: var(--nxt-z-raised);
  /* In a narrow tile the button takes a row of its own: a viewport query cannot see that. */
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-md);
  margin: 0 auto var(--nxt-space-xl);
  max-width: calc(var(--nxt-size-unit) * 68.75);
}

.cx-home-hero-text {
  flex: 1 1 calc(var(--nxt-size-unit) * 12);
  min-width: 0;
}

.cx-home-hero-mark {
  flex: none;
  width: var(--nxt-tile-size-xl);
  height: var(--nxt-tile-size-xl);
  border-radius: var(--nxt-radius-lg);
  background: var(--cx-home-brand-gradient);
  display: grid;
  place-items: center;
  color: var(--nxt-brand-fg);
  box-shadow: var(--nxt-shadow-md);
}

.cx-home-title {
  font-family: var(--nxt-font);
  font-size: var(--nxt-heading-size-1);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-heading-fg);
  letter-spacing: -0.02em;
  line-height: var(--nxt-line-height-tight);
  margin: 0;
}

.cx-home-sub {
  font-size: var(--nxt-font-size-md);
  color: var(--nxt-fg-muted);
  margin: var(--nxt-space-2xs) 0 0;
}

.cx-home-day {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin: var(--nxt-space-xs) 0 0;
}

.cx-home-section {
  position: relative;
  z-index: var(--nxt-z-raised);
  margin: 0 auto var(--nxt-space-xl);
  max-width: calc(var(--nxt-size-unit) * 68.75);
}

.cx-home-section-title {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nxt-fg-subtle);
  margin: 0 0 var(--nxt-space-md);
}

.cx-home-apps {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(calc(var(--nxt-size-unit) * 10), 1fr));
  gap: var(--nxt-space-sm);
}

.cx-home-tips {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(calc(var(--nxt-size-unit) * 13.75), 1fr));
  gap: var(--nxt-space-xs);
}

.cx-home-tip-icon {
  color: var(--nxt-brand-bg);
}
</style>
